import {
  ChangeDealershipInterceptorService,
  CHANGE_DEALERSHIP_INTERCEPTOR_CONFIGS,
  setChangeDealershipDomainConfigs
} from './modules/shared/services/change-dealership-interceptor.service';
import { TokenInterceptorService } from './modules/shared/services/token-intercepter.service';
import { CustomSerializer } from './modules/store/custom-route-serializer';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { SharedModule } from './modules/shared/shared.module';
import { LayoutModule } from './modules/layout/layout.module';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule, NgZone, inject } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { appReducer, AppState, FinishAppInitializer, StartAppInitializer } from './modules/store/app.reducer';
import { EffectsModule } from '@ngrx/effects';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { appEffects } from './modules/store/app.effects';
import { DealerState } from './modules/store/dealer/dealer.reducer';
import { filter, switchMap, take } from 'rxjs/operators';
import * as fromDealerActions from './modules/store/dealer/dealer.actions';
import * as fromDealerSelectors from './modules/store/dealer/dealer.selectors';
import { AuthService } from './modules/shared/services/auth.service';
import { setCurrentCurrency, setCurrentLanguageLocale, setCountryCode, setGlobalTXM, setDmsAndCrmIntegration, setBrand, setUserProfile, setOverrideValues, DD365Roles, DD365DistRoles, setLicenseToVinEnabled } from './modules/shared/services/util.service';
import { AlertService } from './modules/shared/services/alert.service';
import { CalendarModule } from 'angular-calendar';
import { AppointmentService } from './modules/shared/services/appointment.service';
import { TranslateModule, TranslateLoader, TranslateService } from  '@ngx-translate/core';
import { CustomTranslateFactoryService } from './modules/shared/services/custom-translate-factory.service.ts';

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        StoreModule.forRoot(appReducer),
        EffectsModule.forRoot(appEffects),
        StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
        StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }),
        CalendarModule,
        LayoutModule,
        SharedModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: translateLoaderFactory,
                deps: [HttpClient]
            }
        })], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptorService,
            multi: true
        },
        {
            provide: RouterStateSerializer,
            useClass: CustomSerializer
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initApp,
            multi: true,
            deps: [Store, Store, AuthService, AlertService, AppointmentService, TranslateService]
        },
        {
            provide: CHANGE_DEALERSHIP_INTERCEPTOR_CONFIGS,
            useFactory: setChangeDealershipDomainConfigs
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ChangeDealershipInterceptorService,
            deps: [CHANGE_DEALERSHIP_INTERCEPTOR_CONFIGS, AuthService],
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA] })
export class AppModule { }
export function translateLoaderFactory(httpClient: HttpClient) {
  return new CustomTranslateFactoryService(httpClient);
}

// TODO Add promise reject logic
export function initApp(store: Store<AppState>,
  dealerState: Store<DealerState>,
  authService: AuthService,
  alertService: AlertService,
  appointmentService:AppointmentService,
  translateService:TranslateService) {

  // if (environment.env === 'local') {
  //   const dealerProfile: UserProfileModel = {
  //     dealerCd: '20096',
  //     token: environment.MOCK_TOKEN,
  //     roles: ['DD_Enrolled', 'DD_ASP', 'DD_NewVehicleVMSIncludeCost', 'DD_ServiceManagement']
  //   };

  //   const userProfile = new ReplaySubject(1);

  //   // tslint:disable-next-line: no-object-literal-type-assertion
  //   const defaultSubject = {
  //     userProfile
  //   } as SingleSpaProps;

  //   singleSpaPropsSubject.next(defaultSubject);
  //   userProfile.next(dealerProfile);
  // }

    return () => new Promise((resolve, reject) => {
      store.dispatch(new StartAppInitializer());
      authService.getSingleSpaProps().pipe(
        switchMap(props => props.userProfile),
        take(1)).subscribe(userProfile => {
          /* Temporarily keeping Falmouth (20096) as default one */
          // userProfile.dealerCd = '20096';
          const countryCode = userProfile['dealer']?.countryCd;
          const dealerId = userProfile.dealerCd;
          translateService.addLangs(['en-US','es-US','en-MX','es-MX']);
          translateService.setDefaultLang('en-US');
          const browserLang = translateService.getBrowserLang();
          //log to identify translation issue in higher environments based on user's local language
          const localization = userProfile?.localization?.locale;
          translateService.use('en-US');
          if (localization !== browserLang && userProfile?.localization?.locale?.includes('-')) {
            translateService.use(localization).subscribe(data => {
              //If translation files not found for user's local language
              if (data === null) {
                translateService.use('en-US');
              }
            });
          }
          setBrand(dealerId);
          setUserProfile(userProfile);
          if(countryCode){
            setCountryCode(countryCode);
          }
          appointmentService.setUserName(userProfile.userId);
          authService.parseDealerInfo(userProfile.token, dealerId, userProfile.roles);
          authService.getEmployeeRoles().subscribe(roles => {
            appointmentService.setUserRoles(userProfile.roles);
            if([...DD365Roles, ...DD365DistRoles].some(role => roles.includes(role))){
              dealerState.dispatch(new fromDealerActions.LoadDealerDetails({ dealerCode: dealerId }));
              dealerState.dispatch(new fromDealerActions.LoadStatesDetails());
              dealerState.dispatch(new fromDealerActions.LoadCityDetails());
              dealerState.select(fromDealerSelectors.dealerDetails).pipe(
                filter(dealerDetails => dealerDetails.data !== null && dealerDetails.data !== undefined),
                take(1)
              ).subscribe((dealerDetails) => {
                if (!dealerDetails.error) {
                  translateService.get('GLOBAL.LOCALE').subscribe(locale => {
                    setCurrentLanguageLocale(locale);
                  });
                  translateService.get('GLOBAL.CURRENCY').subscribe(currency => {
                    setCurrentCurrency(currency);
                  });
                  setGlobalTXM(dealerDetails.data.isGlobalTXM);
                  setLicenseToVinEnabled(dealerDetails.data['isLicenseToVinEnabled']);
                  setDmsAndCrmIntegration(dealerDetails.data.isAspDmsIntegrationEnabled, dealerDetails.data.isAspCrmIntegrationEnabled);
                  setOverrideValues(dealerDetails.data?.isSpServiceDashboardOverride, dealerDetails.data?.isSpServiceSchedulingOverride,
                    dealerDetails.data?.isSalesAccessibleFeatureFlag, dealerDetails.data?.isServiceAccessibleFeatureFlag,
                    dealerDetails.data?.isPartsAccessible, 
                    dealerDetails.data?.isDeliveryChecklistAccessible);
                    store.dispatch(new FinishAppInitializer());
                  resolve(true);
                } else {
                  store.dispatch(new FinishAppInitializer({ success: false }));
                  console.log('Application can not work as the dealer info is not available.');
                  alertService.showAlert('otherTexts.dealerInfoNotAvailable');
                  reject(new Error('Application can not work as the dealer info is not available.'));
                }
              });
            }
            else {
              resolve(true);
            }
          });
        },
        error => {
          window.location.reload();
        });
    });
}
